import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { BreakdownListNew } from '../../../components/breakdown-list-new/BreakdownListNew';
import { Modal, ProgressBar, ToastContainer, Tooltip, OverlayTrigger } from 'react-bootstrap';
import {
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  Radar,
  ShieldAlert,
  Wifi,
  WifiOff,
} from 'lucide-react';
import Switch from 'react-switch';
import { useOperatorsState } from '../../../services/OperatorsList';
import { WidgetChart } from '../../../components/widget-chart/WidgetChart';
import { ProgressList } from '../../../components/progress-list/ProgressList';
import { DarkButton } from '../../../components/operator-dashboard/dark-button/DarkButton';
import './CVEThreats.scss';
import axios from 'axios';
import { ChangeEvent, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { BreakdownList } from '../../../components/breakdown-list/BreakdownList';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '../../../services/ToastContext';
import { FormControl, FormSelect } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Pagination from 'rc-pagination';
import CustomNextIcon, { TableWidget } from '../../../components/table-widget/TableWidget';
import { CustomPreviousIcon } from '../../../components/table-widget/TableWidget';
import { LoadingSpinner } from '../../../components/loading-spinner/LoadingSpinner';

// Types and Interfaces
interface CveDetails {
  cveNumber: string;
  threatDescription: string;
  cvssRating: string;
}

interface CVE_Threats {
  cveNumber: string;
  threatDescription: string;
  cvssRating: string;
}

interface CveDetailsData {
  cveId: string;
  description: string;
  cvssVersion: string;
  nistBaseScore: string;
  cisaBaseScore: string;
  vilnerabilityCatalog: string;
  vulnerabilityName: string;
  dateAdded: string;
  commonWeaknessEnumeration: null | string;
  softwareConfiguration: null | Array<{ criteria: string; versionEndExcluding: string }>;
}

interface IThreatsDetailsBoxProps {
  cveId: string;
  onBackToList: () => void;
}

interface CVEThreatsProps {
  isConnected: string;
}

// Constants
const SEVERITY_COLORS = {
  Critical: '#101820',
  High: '#FF0000',
  Medium: '#FFFC00',
  Low: '#3BFF00',
} as const;

const SEVERITY_TEXT_COLORS = {
  Critical: '#fff',
  High: '#fff',
  Medium: '#000',
  Low: '#000',
} as const;

const PAGE_SIZE = 10;

// Presentation helpers (display only)
type SeverityKey = 'critical' | 'high' | 'medium' | 'low';

const SEVERITY_LABELS: Record<SeverityKey, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

// CVSS v3 bands: >= 9 critical, >= 7 high, >= 4 medium, otherwise low
const getSeverityFromScore = (score?: string | null): SeverityKey | null => {
  const value = parseFloat(score ?? '');
  if (Number.isNaN(value)) return null;
  if (value >= 9) return 'critical';
  if (value >= 7) return 'high';
  if (value >= 4) return 'medium';
  return 'low';
};

const SeverityChip = ({ score }: { score?: string | null }) => {
  const { t } = useTranslation();
  const severity = getSeverityFromScore(score);
  if (!severity) return null;
  return (
    <span className={`dd-cve-sev ${severity}`}>
      <span className={`bd-sev ${severity}`} aria-hidden="true" />
      {t(`deviceDetails.cve.severity.${severity}`, { defaultValue: SEVERITY_LABELS[severity] })}
    </span>
  );
};

const nvdUrl = (cveId: string) => `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(cveId)}`;

// ThreatsDetailsBox Component
export const ThreatsDetailsBox = ({ cveId, onBackToList }: IThreatsDetailsBoxProps) => {
  const [data, setData] = useState<CveDetailsData>();
  const [isDataFetched, setIsDataFetched] = useState<boolean>(true);
  const { operatorId } = useParams();
  const { t } = useTranslation();

  const fetchDetails = useCallback(async () => {
    try {
      const response = await axios.get(`v2/op/device-cves/nvd-cve-threats/${operatorId}/${cveId}`);
      setIsDataFetched(false);
      setData(response.data.cveThreats);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [operatorId, cveId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const backButton = (
    <button type="button" className="bd-btn secondary dd-cve-back" onClick={onBackToList}>
      <ArrowLeft size={16} strokeWidth={1.9} aria-hidden="true" />
      {t('deviceDetails.cve.backToList')}
    </button>
  );

  const renderSoftwareConfigurations = () => {
    if (!data?.softwareConfiguration || data.softwareConfiguration.length === 0) {
      return (
        <div className="dd-cve-table-wrap">
          <table className="dd-cve-raw-table">
            <thead>
              <tr>
                <th scope="col">{t('deviceDetails.cve.configuration')}</th>
                <th scope="col">{t('deviceDetails.cve.upToExcluding')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="bd-mono dd-cve-cpe">cpe:2.3:a:google:chrome:*:*:*:*:*:*:*:*</td>
                <td className="bd-mono">124.0.6367.201</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="dd-cve-table-wrap">
        <table className="dd-cve-raw-table" key="Table">
          <thead>
            <tr>
              <th scope="col">{t('deviceDetails.cve.configuration')}</th>
              <th scope="col">{t('deviceDetails.cve.upToExcluding')}</th>
            </tr>
          </thead>
          <tbody>
            {data.softwareConfiguration.map((result, index) => (
              <tr key={index}>
                <td className="bd-mono dd-cve-cpe">{result.criteria}</td>
                <td className="bd-mono">{result.versionEndExcluding}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isDataFetched) {
    return (
      <LoadingSpinner text={cveId ? `${t('deviceDetails.cve.loadingDetails')} · ${cveId}` : t('deviceDetails.cve.loadingDetails')} />
    );
  }

  if (!data) {
    return (
      <div className="dd-cve-state">
        <span className="dd-cve-state-icon warn" aria-hidden="true">
          <AlertTriangle size={22} strokeWidth={1.75} />
        </span>
        <span className="bd-h3">{t('deviceDetails.cve.noDetails')}</span>
        <span className="bd-meta">
          <Trans i18nKey="deviceDetails.cve.noDetailsBody" values={{ cveId }} components={{ mono: <span className="bd-mono" /> }} />
        </span>
        {backButton}
      </div>
    );
  }

  const [nistScore, severityLevel] = data.cvssVersion.split(' ');
  const isInCisaCatalog = data.vilnerabilityCatalog !== null;
  const hasCommonWeaknessEnumeration = data.commonWeaknessEnumeration?.split('-')[2] !== 'noinfo' && data.commonWeaknessEnumeration !== null;

  return (
    <div className="dd-cve-detail">
      <header className="dd-cve-detail-hd">
        <div className="dd-cve-detail-title">
          <span className="bd-field-label">{t('deviceDetails.cve.detailsLabel')}</span>
          <div className="dd-cve-detail-id-row">
            <h2 className="bd-h2 bd-mono dd-cve-detail-id">{data.cveId}</h2>
            <SeverityChip score={nistScore} />
            <a
              className="bd-iconbtn dd-cve-extlink"
              href={nvdUrl(data.cveId)}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={t('deviceDetails.cve.openOnNvd', { cveId: data.cveId })}
              title={t('deviceDetails.cve.viewOnNvd')}
            >
              <ExternalLink size={15} strokeWidth={1.9} aria-hidden="true" />
            </a>
          </div>
          <p className="bd-meta m-0">{t('deviceDetails.cve.detailsSubtitle')}</p>
        </div>
        {backButton}
      </header>

      <section className="dd-cve-block" aria-labelledby="dd-cve-desc-title">
        <h3 className="bd-field-label" id="dd-cve-desc-title">{t('deviceDetails.cve.description')}</h3>
        <p className="bd-body dd-cve-desc">{data.description}</p>
      </section>

      <div className="dd-cve-stats">
        <div className="dd-cve-stat">
          <span className="bd-field-label">{t('deviceDetails.cve.cvssVersion')}</span>
          <span className="bd-mono dd-cve-stat-val">{severityLevel || '--'}</span>
        </div>
        <div className="dd-cve-stat">
          <span className="bd-field-label">{t('deviceDetails.cve.nistBaseScore')}</span>
          <div className="dd-cve-stat-row">
            <span className="bd-stat-num dd-cve-stat-num">{nistScore || '--'}</span>
            <SeverityChip score={nistScore} />
          </div>
        </div>
        <div className="dd-cve-stat">
          <span className="bd-field-label">{t('deviceDetails.cve.cisaBaseScore')}</span>
          <div className="dd-cve-stat-row">
            <span className={`bd-stat-num dd-cve-stat-num ${data.nistBaseScore}`}>{data.nistBaseScore}</span>
            <SeverityChip score={data.nistBaseScore} />
          </div>
        </div>
      </div>

      <div className="dd-cve-fact">
        <span className="bd-body dd-cve-fact-label">{t('deviceDetails.cve.inCisaCatalog')}</span>
        <span className={`bd-chip ${isInCisaCatalog ? 'danger' : 'mint'}`}>
          <span className="dot" />
          {isInCisaCatalog ? t('deviceDetails.cve.yes') : t('deviceDetails.cve.no')}
        </span>
      </div>

      <div className="dd-cve-table-wrap">
        <table className="dd-cve-raw-table">
          <thead>
            <tr>
              <th scope="col">{t('deviceDetails.cve.vulnerabilityName')}</th>
              <th scope="col">{t('deviceDetails.cve.dateAdded')}</th>
              <th scope="col">{t('deviceDetails.cve.dueDate')}</th>
              <th scope="col">{t('deviceDetails.cve.requiredAction')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="dd-cve-strong">{data.vulnerabilityName || t('deviceDetails.cve.unknown')}</td>
              <td className="bd-mono">{data.dateAdded}</td>
              <td className="bd-mono dd-cve-dim">--</td>
              <td className="dd-cve-dim">
                <div className="dd-cve-stack">
                  <span>--</span>
                  <span>--</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="dd-cve-fact">
        <span className="bd-body dd-cve-fact-label">{t('deviceDetails.cve.cweLink')}</span>
        {hasCommonWeaknessEnumeration ? (
          <a
            href={`https://cwe.mitre.org/data/definitions/${data.commonWeaknessEnumeration?.split('-')[1]}.html`}
            target="_blank"
            rel="noreferrer noopener"
            className="dd-cve-link bd-mono"
          >
            {data.commonWeaknessEnumeration}
            <ExternalLink size={13} strokeWidth={1.9} aria-hidden="true" />
          </a>
        ) : (
          <span className="bd-meta">{t('deviceDetails.cve.noCwe')}</span>
        )}
      </div>

      <section className="dd-cve-block" aria-labelledby="dd-cve-sw-title">
        <h3 className="bd-h3" id="dd-cve-sw-title">{t('deviceDetails.cve.affectedSoftware')}</h3>
        {renderSoftwareConfigurations()}
      </section>
    </div>
  );
};

// CVEThreats Main Component
export const CVEThreats = ({ isConnected }: CVEThreatsProps) => {
  const { showToastMessage } = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { operatorId, deviceId } = useParams();
  const [cveThreats, setCveThreats] = useState<CVE_Threats[]>([]);
  const [filteredCveThreats, setFilteredCveThreats] = useState<CVE_Threats[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [inputSearchField, setInputSearchField] = useState<string>('');
  const [totalEntries, setTotalEntries] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedCve, setSelectedCve] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  // Form schema for validation
  const formSchema = z.object({
    filterApplied: z.enum(['', 'under12', 'earlyTeens', 'youngAdults', 'noShield']),
  });

  type FormInputs = z.infer<typeof formSchema>;

  const { watch } = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
  });

  const filterApplied = watch('filterApplied');

  // Fetch CVE threats data
  const getCveThreats = useCallback(async (page: number = currentPage) => {
    try {
      const response = await axios.get(
        `/v2/op/device/${operatorId}/${deviceId}/device-cves?page=${page}&pageSize=${PAGE_SIZE}&searchQuery=&order=createdAt,ASC`
      );
      setCveThreats(response.data.entries);
      setTotalEntries(response.data.totalEntries);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching CVE threats:', error);
      setIsLoading(false);
    }
  }, [operatorId, deviceId, currentPage]);

  useEffect(() => {
    // getCveThreats();
  }, [getCveThreats]);

  // Handle filter changes
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    setSelectedFilters(prevFilters =>
      checked ? [...prevFilters, value] : prevFilters.filter(filter => filter !== value)
    );
  };

  // Filter threats based on selected filters and search term
  const filterThreats = useCallback(() => {
    let result = cveThreats;

    // Apply severity filters
    if (selectedFilters.length > 0) {
      result = result.filter(threat => selectedFilters.includes(threat.cvssRating));
    }

    // Apply search filter
    if (inputSearchField) {
      const searchTerm = inputSearchField.toUpperCase();
      result = result.filter(threat => threat.cveNumber.includes(searchTerm));
    }

    // Handle no results
    if (result.length === 0) {
      result = [{ cveNumber: 'No Record Found', threatDescription: 'No Record Found', cvssRating: '' }];
    }

    setFilteredCveThreats(result);
    setIsLoading(false);
  }, [cveThreats, selectedFilters, inputSearchField]);

  // Debounced search and filter
  useEffect(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      filterThreats();
    }, 500);

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [filterThreats]);

  // Handle search input change
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.replace(/\s/g, '').toUpperCase();
    setInputSearchField(searchTerm);
    setIsLoading(true);
  };

  // Handle scan device action
  const scanDeviceNow = () => {
    if (isConnected !== 'NOT_CONNECTED') {
      showToastMessage(t('deviceDetails.cve.scanSubmitted'));
    }
  };

  // Handle pagination
  const fetchLogs = async (pageIndex: number) => {
    setIsLoading(true);
    setCurrentPage(pageIndex);
    await getCveThreats(pageIndex);
  };

  // Handle threat details selection
  const handleCveSelect = (cveId: string) => {
    if (cveId !== 'No Record Found') {
      setSelectedCve(cveId);
    }
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedCve(null);
  };

  // Render tooltip for long text
  const renderTooltip = (text: string) => (
    <Tooltip id="tooltip">{text}</Tooltip>
  );

  const getTdValue = (value: string) => (
    <OverlayTrigger placement="top" overlay={renderTooltip(value)}>
      <div className="main border-0 maxWidth" style={{ maxWidth: '700px' }}>
        <div className="border-0 m-0">{value}</div>
      </div>
    </OverlayTrigger>
  );

  // Render highlighted search results
  const renderHighlightedCveNumber = (cveNumber: string, searchTerm: string) => {
    if (!searchTerm) return cveNumber;

    const index = cveNumber.indexOf(searchTerm);
    if (index === -1) return cveNumber;

    const beforeMatch = cveNumber.slice(0, index);
    const matchedPart = cveNumber.slice(index, index + searchTerm.length);
    const afterMatch = cveNumber.slice(index + searchTerm.length);

    return (
      <>
        {beforeMatch}
        <span className="highlight">{matchedPart}</span>
        {afterMatch}
      </>
    );
  };

  // If a CVE is selected, show the details view instead of the table
  if (selectedCve) {
    return (
      <section className="dd-cve" id="cpe-details-section">
        <ThreatsDetailsBox cveId={selectedCve} onBackToList={handleBackToList} />
      </section>
    );
  }

  const isOffline = isConnected === 'NOT_CONNECTED';
  const offlineHint = t('deviceDetails.cve.offlineHint');

  const scanButton = (
    <button
      type="button"
      className="bd-btn primary dd-cve-scan"
      onClick={scanDeviceNow}
      disabled={isOffline}
      aria-describedby={isOffline ? 'dd-cve-scan-hint' : undefined}
    >
      <Radar size={16} strokeWidth={1.9} aria-hidden="true" />
      {t('deviceDetails.cve.scanNow')}
    </button>
  );

  return (
    <section className="dd-cve" id="cpe-details-section">
      <header className="dd-cve-hd">
        <div className="dd-cve-hd-text">
          <div className="dd-cve-hd-title">
            <span className="dd-cve-hd-icon" aria-hidden="true">
              <ShieldAlert size={18} strokeWidth={1.8} />
            </span>
            <h2 className="bd-h2">{t('deviceDetails.cve.listTitle')}</h2>
            {isConnected !== undefined && isConnected !== null && isConnected !== '' && (
              <span className={`bd-chip ${isOffline ? 'danger' : 'mint'}`}>
                {isOffline ? (
                  <WifiOff size={12} strokeWidth={2} aria-hidden="true" />
                ) : (
                  <Wifi size={12} strokeWidth={2} aria-hidden="true" />
                )}
                {isOffline ? t('deviceDetails.cve.deviceOffline') : t('deviceDetails.cve.deviceOnline')}
              </span>
            )}
          </div>
          <p className="bd-body dd-cve-hd-desc">{t('deviceDetails.cve.listDescription')}</p>
        </div>

        <div className="dd-cve-hd-actions">
          {isOffline ? (
            <OverlayTrigger placement="top" overlay={renderTooltip(offlineHint)}>
              {/* disabled buttons swallow pointer events; the wrapper carries the tooltip */}
              <span className="dd-cve-scan-wrap" tabIndex={0}>
                {scanButton}
              </span>
            </OverlayTrigger>
          ) : (
            scanButton
          )}
          {isOffline && (
            <span className="bd-meta dd-cve-scan-hint" id="dd-cve-scan-hint">
              <WifiOff size={12} strokeWidth={2} aria-hidden="true" />
              {t('deviceDetails.cve.unavailableOffline')}
            </span>
          )}
        </div>
      </header>

      <div className="dd-cve-table">
        <TableWidget
          hasSearch={true}
          url={`v2/op/device/${operatorId}/${deviceId}/device-cves`}
          countPerPage={10}
          title={t('deviceDetails.cve.tableTitle')}
          searchPositionRight={true}
          hasCategoryFilter={true}
          needCategories={true}
          isDns={true}
          isCve={true}
          onRowSelect={(rowdata: any) => handleCveSelect(rowdata.key['Vuln ID'])} />
      </div>
    </section>
  );
};

export default CVEThreats;
